import { CascadiaColorTypes } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { RequirementsItem as RequirementItemType } from "./types";
import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";
import { useColors } from "../../hooks/useColors";
import { LinkButton } from "../link/LinkButton";

export interface RequirementItemProps {
  requirementsItem: RequirementItemType;
  testID?: string;
}

const requirementsItemIcons = {
  normal: <Icon name="bullet" type="custom" color="icon-base" />,
  success: (
    <Icon name="check" type="mini" color="icon-success" padding="space-05" />
  ),
  error: (
    <Icon name="error" type="mini" color="icon-danger" padding="space-05" />
  ),
};

export function RequirementsItem({
  requirementsItem,
  testID = "requirements-item",
}: RequirementItemProps) {
  const { t } = useTranslation();
  const colors = useColors();

  const styles = getStyles(colors);

  const {
    text,
    style = "normal",
    recommended,
    recommendedText = t("recommended"),
    link,
  } = requirementsItem;

  const textClassName = css(styles.textStyles[style]);

  return (
    <li className={css(styles.requirementsItemWrapper)} data-testid={testID}>
      {requirementsItemIcons[style]}
      <Text
        className={textClassName}
        style="form/body-secondary"
        color="text-weak"
      >
        {text}
        {recommended ? ` (${recommendedText})` : ""}
      </Text>
      {link && (
        <LinkButton onPress={link.onClick} testID={`${testID}-link`}>
          <Text
            className={textClassName}
            style="form/body-secondary"
            color="text-weak"
          >
            {link.text}
          </Text>
        </LinkButton>
      )}
    </li>
  );
}

const getStyles = (colors: CascadiaColorTypes) => {
  return {
    textStyles: {
      normal: css({
        color: "var(--text-weak)",
        marginLeft: "var(--space-1)",
      }),
      error: css({
        color: "var(--text-danger)",
        marginLeft: "var(--space-1)",
      }),
      success: css({
        color: "var(--text-success)",
        marginLeft: "var(--space-1)",
      }),
    },
    requirementsItemWrapper: css({
      display: "flex",
      alignContent: "center",
      marginTop: "var(--space-05)",
      marginBottom: "var(--space-05)",
    }),
  };
};
